(() => {
  if (window.SenfonIndustrySolutionsLoaded) return;
  window.SenfonIndustrySolutionsLoaded = true;

  const controllers = new Set();
  const desktopMotion = window.matchMedia('(min-width: 1024px) and (prefers-reduced-motion: no-preference)');

  const init = (section) => {
    if (!section || section.dataset.sisReady === 'true') return;
    section.dataset.sisReady = 'true';

    const tabs = Array.from(section.querySelectorAll('[data-sis-tab]'));
    const panels = Array.from(section.querySelectorAll('[data-sis-panel]'));
    const tabsScroller = section.querySelector('[data-sis-tabs]');
    const tabsPrev = section.querySelector('[data-sis-tabs-prev]');
    const tabsNext = section.querySelector('[data-sis-tabs-next]');
    if (!tabs.length || !panels.length || !tabsScroller) return;

    let animationFrameId = null;
    let restartTimerId = null;
    let lastFrameTime = 0;
    let sectionVisible = false;
    let interactionPauseUntil = 0;
    let autoGallery = null;

    const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const activePanel = () => panels.find((panel) => !panel.hidden);
    const activeGallery = () => activePanel()?.querySelector('[data-sis-gallery]');
    const scrollable = (element) => element && element.scrollWidth - element.clientWidth > 8;
    const originalCards = (gallery) => Array.from(
      gallery?.querySelectorAll('.senfon-industry-solutions__card:not([data-sis-clone])') || []
    );
    const loopDistance = (gallery) => Number(gallery?.dataset.sisLoopDistance || 0);
    const loopReady = (gallery) => gallery?.dataset.sisLoopReady === 'true' && loopDistance(gallery) > 8;
    // Keep the complete real-project gallery, but do not run a continuous
    // requestAnimationFrame loop. Visitors retain manual controls and lazy
    // image hydration without permanent main-thread/battery cost.
    const loopAllowed = () => false;
    const hydratePanel = (panel) => {
      if (!panel || panel.dataset.sisImagesReady === 'true') return;
      panel.querySelectorAll('img[data-sis-src]').forEach((image) => {
        if (image.dataset.sisSrcset) image.srcset = image.dataset.sisSrcset;
        image.src = image.dataset.sisSrc;
        image.removeAttribute('data-sis-src');
        image.removeAttribute('data-sis-srcset');
      });
      panel.dataset.sisImagesReady = 'true';
    };
    const hydrateActivePanel = () => hydratePanel(activePanel());

    const setDisabled = (button, disabled) => {
      if (!button) return;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    };
    const updatePair = (element, previous, next) => {
      if (!element) return;
      const maxLeft = Math.max(0, element.scrollWidth - element.clientWidth);
      if (maxLeft <= 8) {
        setDisabled(previous, true);
        setDisabled(next, true);
        return;
      }
      setDisabled(previous, element.scrollLeft <= 4);
      setDisabled(next, element.scrollLeft >= maxLeft - 4);
    };
    const updateLoopMetric = (gallery) => {
      const firstOriginal = originalCards(gallery)[0];
      const firstClone = gallery?.querySelector('[data-sis-clone]');
      const distance = firstOriginal && firstClone ? firstClone.offsetLeft - firstOriginal.offsetLeft : 0;
      if (distance > 8) gallery.dataset.sisLoopDistance = String(distance);
      else delete gallery?.dataset.sisLoopDistance;
    };
    const disableLoop = (gallery) => {
      if (!gallery || gallery.dataset.sisLoopReady !== 'true') return;
      const previousLeft = gallery.scrollLeft;
      gallery.querySelectorAll('[data-sis-clone]').forEach((card) => card.remove());
      delete gallery.dataset.sisLoopReady;
      delete gallery.dataset.sisLoopDistance;
      gallery.scrollLeft = Math.min(previousLeft, Math.max(0, gallery.scrollWidth - gallery.clientWidth));
    };
    const enableLoop = (gallery) => {
      if (!gallery || !loopAllowed()) return false;
      if (gallery.dataset.sisLoopReady === 'true') {
        updateLoopMetric(gallery);
        if (loopReady(gallery)) return true;
        disableLoop(gallery);
      }

      const cards = originalCards(gallery);
      if (cards.length < 2 || !scrollable(gallery)) return false;

      const copies = document.createDocumentFragment();
      cards.forEach((card) => {
        const copy = card.cloneNode(true);
        copy.dataset.sisClone = 'true';
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('img').forEach((image) => {
          image.alt = '';
          image.setAttribute('aria-hidden', 'true');
          image.setAttribute('loading', 'lazy');
        });
        copies.append(copy);
      });
      gallery.append(copies);
      gallery.dataset.sisLoopReady = 'true';
      updateLoopMetric(gallery);

      if (loopReady(gallery)) return true;
      disableLoop(gallery);
      return false;
    };
    const syncLoop = () => {
      const currentGallery = activeGallery();
      panels.forEach((panel) => {
        const gallery = panel.querySelector('[data-sis-gallery]');
        if (!gallery) return;
        if (gallery === currentGallery && loopAllowed()) enableLoop(gallery);
        else disableLoop(gallery);
      });
    };
    const updateControls = () => {
      updatePair(tabsScroller, tabsPrev, tabsNext);
      panels.forEach((panel) => {
        const gallery = panel.querySelector('[data-sis-gallery]');
        const previous = panel.querySelector('[data-sis-gallery-prev]');
        const next = panel.querySelector('[data-sis-gallery-next]');
        if (loopReady(gallery)) {
          setDisabled(previous, false);
          setDisabled(next, false);
        } else {
          updatePair(gallery, previous, next);
        }
      });
    };
    const clearAutoplay = () => {
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
      autoGallery = null;
      lastFrameTime = 0;
    };
    const getStep = (element) => {
      const card = element?.querySelector('.senfon-industry-solutions__card');
      if (card) {
        const styles = window.getComputedStyle(element);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return card.getBoundingClientRect().width + gap;
      }
      return Math.max(180, element?.clientWidth * 0.72 || 0);
    };
    const moveWithinLoop = (gallery, target) => {
      const distance = loopDistance(gallery);
      if (!distance) return target;
      let next = target % distance;
      if (next < 0) next += distance;
      return next;
    };
    const scrollElement = (element, direction, amount = getStep(element)) => {
      if (!scrollable(element)) return;
      if (loopReady(element)) {
        const distance = loopDistance(element);
        const target = element.scrollLeft + direction * amount;
        if (target >= 0 && target < distance) {
          element.scrollBy({ left: direction * amount, behavior: reducedMotion() ? 'auto' : 'smooth' });
        } else {
          element.scrollLeft = moveWithinLoop(element, target);
        }
        return;
      }
      element.scrollBy({ left: direction * amount, behavior: reducedMotion() ? 'auto' : 'smooth' });
    };
    const canAutoplay = () => {
      const gallery = activeGallery();
      return Boolean(
        loopAllowed() &&
          !document.hidden &&
          Date.now() >= interactionPauseUntil &&
          loopReady(gallery)
      );
    };
    const moveGallery = (time) => {
      if (!canAutoplay()) {
        animationFrameId = null;
        autoGallery = null;
        lastFrameTime = 0;
        return;
      }
      const gallery = activeGallery();
      const distance = loopDistance(gallery);
      if (!lastFrameTime) lastFrameTime = time;
      const elapsed = Math.min(48, time - lastFrameTime);
      lastFrameTime = time;

      // 20px/秒：可感知但不会抢走阅读注意力。到复制的首张图时无缝回到原始首张图。
      const nextLeft = moveWithinLoop(gallery, gallery.scrollLeft + (20 * elapsed) / 1000);
      autoGallery = gallery;
      gallery.scrollLeft = nextLeft;
      animationFrameId = window.requestAnimationFrame(moveGallery);
    };
    const refreshAutoplay = () => {
      clearAutoplay();
      syncLoop();
      updateControls();
      if (canAutoplay()) animationFrameId = window.requestAnimationFrame(moveGallery);
    };
    const pauseForInteraction = () => {
      interactionPauseUntil = Date.now() + 6000;
      if (restartTimerId) window.clearTimeout(restartTimerId);
      refreshAutoplay();
      restartTimerId = window.setTimeout(() => {
        restartTimerId = null;
        refreshAutoplay();
      }, 6100);
    };
    const activateTab = (tab, moveFocus = false) => {
      const targetId = tab.getAttribute('aria-controls');
      tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => {
        const selected = panel.id === targetId;
        panel.hidden = !selected;
        if (selected) {
          hydratePanel(panel);
          const gallery = panel.querySelector('[data-sis-gallery]');
          if (gallery) gallery.scrollLeft = 0;
        }
      });
      tab.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      if (moveFocus) tab.focus();
      window.requestAnimationFrame(refreshAutoplay);
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        activateTab(tabs[nextIndex], true);
      });
    });

    tabsPrev?.addEventListener('click', () => scrollElement(tabsScroller, -1, Math.max(180, tabsScroller.clientWidth * 0.7)));
    tabsNext?.addEventListener('click', () => scrollElement(tabsScroller, 1, Math.max(180, tabsScroller.clientWidth * 0.7)));

    panels.forEach((panel) => {
      const gallery = panel.querySelector('[data-sis-gallery]');
      const previous = panel.querySelector('[data-sis-gallery-prev]');
      const next = panel.querySelector('[data-sis-gallery-next]');
      previous?.addEventListener('click', () => {
        pauseForInteraction();
        scrollElement(gallery, -1);
      });
      next?.addEventListener('click', () => {
        pauseForInteraction();
        scrollElement(gallery, 1);
      });
      if (gallery) {
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((eventName) => {
          gallery.addEventListener(eventName, pauseForInteraction, { passive: true });
        });
        gallery.addEventListener('scroll', () => {
          if (autoGallery !== gallery) window.requestAnimationFrame(updateControls);
        }, { passive: true });
      }
    });
    tabsScroller.addEventListener('scroll', () => window.requestAnimationFrame(updateControls), { passive: true });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          sectionVisible = entry.isIntersecting;
          if (entry.isIntersecting) hydrateActivePanel();
          refreshAutoplay();
        },
        { rootMargin: '900px 0px', threshold: 0 }
      );
      observer.observe(section);
    } else {
      sectionVisible = true;
      hydrateActivePanel();
    }
    if ('ResizeObserver' in window) new ResizeObserver(refreshAutoplay).observe(section);
    window.addEventListener('resize', refreshAutoplay, { passive: true });

    const controller = { refresh: refreshAutoplay };
    controllers.add(controller);
    window.requestAnimationFrame(refreshAutoplay);
  };

  const boot = (scope = document) => scope.querySelectorAll?.('[data-sis]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('visibilitychange', () => controllers.forEach((controller) => controller.refresh()));
  if (desktopMotion.addEventListener) desktopMotion.addEventListener('change', () => controllers.forEach((controller) => controller.refresh()));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
